import {BillingConfigFormValues} from "../components/forms/BillingConfigForm";
import {Config} from "sharedlibs";
import {getConfig, updateConfig} from "../api/config";

/** The billing config is invalid and was not saved; the message says why. */
export class ConfigValidationError extends Error {}

export default class ConfigService {

  static async getConfig(): Promise<Config> {
    return await getConfig()
  }

  /**
   * Saves the billing form and returns the config as the server stored it.
   * Throws when nothing was saved: a ConfigValidationError for an invalid form,
   * any other error when the request failed.
   */
  static async updateConfig(formObject: BillingConfigFormValues, oldConfig: Config): Promise<Config> {
    const config = this.mapBillingFormToConfig(formObject, oldConfig)

    if (!ConfigService.checkShareRevenues(config, "implants")) {
      throw new ConfigValidationError('Die Summe der Implantatumsatzbeteiligungen muss 100% ergeben')
    }

    if (!ConfigService.checkShareRevenues(config, "sensors")) {
      throw new ConfigValidationError('Die Summe der Umsatzbeteiligungen der externen Sensoren muss 100% ergeben')
    }

    return await updateConfig(config)
  }

  static mapConfigToBillingConfigForm(config: Config): BillingConfigFormValues {
    return {
      semdatexFixedCosts: config.semdatexFixedCosts.toString(),
      getemedFixedCosts: config.getemedFixedCosts.toString(),
      getemedHotlinePrice: config.getemedHotlinePrice.toString(),
      semdatexImplantRevenueShare: config.implantRevenueShare.semdatex.toString(),
      biotronikImplantRevenueShare: config.implantRevenueShare.biotronik.toString(),
      DocCirrusImplantRevenueShare: config.implantRevenueShare.docCirrus.toString(),
      semdatexExternalSensorsRevenueShare: config.extSensorsRevenueShare.semdatex.toString(),
      getemedExternalSensorsRevenueShare: config.extSensorsRevenueShare.getemed.toString(),
      biotronikExternalSensorsRevenueShare: config.extSensorsRevenueShare.biotronik.toString(),
      DocCirrusExternalSensorsRevenueShare: config.extSensorsRevenueShare.docCirrus.toString(),
      sapMaterialExtSensors: config.sapMaterialNumbers?.extSensors ?? '',
      sapMaterialExtSensorsGetemed: config.sapMaterialNumbers?.extSensorsGetemed ?? '',
      sapMaterialImplantsGba: config.sapMaterialNumbers?.implantsGba ?? '',
      sapMaterialImplantsNonGba: config.sapMaterialNumbers?.implantsNonGba ?? '',
    }
  }

  private static mapBillingFormToConfig(billingFormData: BillingConfigFormValues, oldConfig: Config): Config {
    return {
      semdatexFixedCosts: parseFloat(billingFormData.semdatexFixedCosts),
      getemedFixedCosts: parseFloat(billingFormData.getemedFixedCosts),
      getemedHotlinePrice: parseFloat(billingFormData.getemedHotlinePrice),
      lastDeviceCountUpdate: oldConfig.lastDeviceCountUpdate,
      monthlyProductExportRecipients: oldConfig.monthlyProductExportRecipients ?? [],
      lowImplantsReportRecipients: oldConfig.lowImplantsReportRecipients ?? [],
      tmzDailyReportRecipients: oldConfig.tmzDailyReportRecipients ?? [],
      sapMaterialNumbers: {
        extSensors: billingFormData.sapMaterialExtSensors.trim(),
        extSensorsGetemed: billingFormData.sapMaterialExtSensorsGetemed.trim(),
        implantsGba: billingFormData.sapMaterialImplantsGba.trim(),
        implantsNonGba: billingFormData.sapMaterialImplantsNonGba.trim(),
      },
      implantRevenueShare: {
        semdatex: parseFloat(billingFormData.semdatexImplantRevenueShare),
        biotronik: parseFloat(billingFormData.biotronikImplantRevenueShare),
        docCirrus: parseFloat(billingFormData.DocCirrusImplantRevenueShare)
      },
      extSensorsRevenueShare: {
        semdatex: parseFloat(billingFormData.semdatexExternalSensorsRevenueShare),
        getemed: parseFloat(billingFormData.getemedExternalSensorsRevenueShare),
        biotronik: parseFloat(billingFormData.biotronikExternalSensorsRevenueShare),
        docCirrus: parseFloat(billingFormData.DocCirrusExternalSensorsRevenueShare)
      }
    }
  }

  /**
   * Checks if the revenue shares of either implants or sensors are valid. The sum must be 100 (percent).
   * @param config Config object which should be checked
   * @param subject Either "implants" or "sensors" depending on which revenue shares should be checked
   */
  static checkShareRevenues(config: Config, subject: "implants" | "sensors") {
    return subject === "implants" ?
      config.implantRevenueShare.biotronik + config.implantRevenueShare.docCirrus + config.implantRevenueShare.semdatex === 100 :
      config.extSensorsRevenueShare.semdatex + config.extSensorsRevenueShare.getemed + config.extSensorsRevenueShare.biotronik + config.extSensorsRevenueShare.docCirrus === 100
  }
}
